import apiFetch from '@wordpress/api-fetch';
import { useBlockProps } from '@wordpress/block-editor';
import { Spinner } from '@wordpress/components';
import React, { useEffect, useRef, useState } from '@wordpress/element';
import CalculatorCreation from './components/CalculatorCreation';
import CalculatorInspector from './components/CalculatorInspector';
import CalculatorModal from './components/CalculatorModal';
import './editor.scss';
import { fetchApi } from './utils/fetchApi';

// Production URL
const ORIGIN = 'https://www.convertcalculator.com';

const BASE_URL = `${ORIGIN}/app/embeddables`;
const BASE_API_URL = `${ORIGIN}/api`;

const UNMISTAKABLE_CHARS = '23456789ABCDEFGHJKLMNPQRSTWXYZabcdefghijkmnopqrstuvwxyz';

const isValidCalculatorId = (id: string, expectedLength = 17): boolean => {
  const regex = new RegExp(`^[${UNMISTAKABLE_CHARS}]{${expectedLength}}$`);
  return regex.test(id);
};

interface Attributes {
  calculatorId: string;
}

interface EditProps {
  attributes: Attributes;
  setAttributes: (attrs: Partial<Attributes>) => void;
}

export default function Edit({ attributes, setAttributes }: EditProps): JSX.Element {
  const instanceId = useRef(Math.random().toString(36).substring(2, 15));

  const [localCalcId, setLocalCalcId] = useState<string>('');
  const [renderedHTML, setRenderedHTML] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [iFrameUrl, setIFrameUrl] = useState<string>('');
  const [isOpen, setOpen] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const blockProps = useBlockProps();
  const { calculatorId } = attributes;

  useEffect(() => {
    setLocalCalcId(calculatorId || '');
  }, [calculatorId]);

  useEffect(() => {
    if (calculatorId) {
      setIsLoading(true);
      apiFetch({
        path: '/wp/v2/block-render/convertcalculator/embed',
        method: 'POST',
        data: { attributes: { calculatorId } },
      })
        .then((response: any) => {
          setRenderedHTML(response.rendered);
        })
        .catch((error: any) => {
          console.error('Error fetching render:', error);
          setRenderedHTML('<p>Error loading preview</p>');
        })
        .finally(() => setIsLoading(false));
    }
  }, [calculatorId]);

  const handleOpenModal = (url: string) => {
    setIFrameUrl(`${url}?blockId=${instanceId.current}`);
    setOpen(true);
  };

  useEffect(() => {
    const abortController = new AbortController();
    const messageHandler = (event: MessageEvent) => {
      const data = event.data;
      if (
        !data.payload ||
        data.payload.source !== 'convertcalculator-website' ||
        data.blockId !== instanceId.current
      ) {
        return;
      }

      switch (data.type) {
        case 'calculator-embed-click':
        case 'template-embed-click':
          setOpen(false);
          setAttributes({ calculatorId: data.calculatorId || '' });
          setLocalCalcId(data.calculatorId || '');
          break;
        case 'embed-calculator':
          setAttributes({ calculatorId: data.calculatorId || '' });
          setLocalCalcId(data.calculatorId || '');
          break;
        default:
          break;
      }
    };

    window.addEventListener('message', messageHandler, {
      signal: abortController.signal,
    });
    return () => {
      abortController.abort();
    };
  }, [setAttributes]);

  useEffect(() => {
    if (localCalcId && isValidCalculatorId(localCalcId)) {
      const timeout = setTimeout(() => {
        fetchApi({
          url: `${BASE_API_URL}/calculators/validate-calculator/?calculatorId=${localCalcId}`,
          method: 'GET',
        })
          .then((res: any) => {
            if (res?.exists) {
              setErrorMessage('');
            } else {
              setErrorMessage('No calculator found with this id.');
            }
          })
          .catch((err: any) => {
            if (err.status === 400) {
              setErrorMessage('No calculator found with this id.');
            } else {
              console.error('Error checking calculator:', err);
            }
          });
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [localCalcId]);

  const handleEmbed = () => {
    if (!isValidCalculatorId(localCalcId)) {
      setErrorMessage(
        'Invalid Calculator ID. Please navigate to Share → Embed in Convert_ to copy your ID.',
      );
      return;
    }
    if (errorMessage) {
      return;
    }
    setAttributes({ calculatorId: localCalcId });
  };

  const handleSelectTemplate = () => {
    handleOpenModal(`${BASE_URL}/create/templates/`);
  };

  const handleCreateCalculator = async () => {
    handleOpenModal(`${BASE_URL}/create/create-new-calculator/`);
  };

  const handleSelectCustomCalculators = () => {
    handleOpenModal(`${BASE_URL}/calculator-overview/`);
  };

  const handleEdit = async () => {
    handleOpenModal(
      `${BASE_URL}/calculators/${localCalcId}/create-calculator-from-template/`,
    );
  };

  const refreshCalculatorView = () => {
    if (calculatorId) {
      setIsLoading(true);
      apiFetch({
        path: '/wp/v2/block-render/convertcalculator/embed',
        method: 'POST',
        data: { attributes: { calculatorId } },
      })
        .then((response: any) => {
          setRenderedHTML(response.rendered);
        })
        .catch((error: any) => {
          console.error('Error fetching render:', error);
          setRenderedHTML('<p>Error loading preview</p>');
        })
        .finally(() => setIsLoading(false));
    }
  };

  const handleModalClose = () => {
    setOpen(false);
    refreshCalculatorView();
  };

  return (
    <>
      <CalculatorModal
        isOpen={isOpen}
        iFrameUrl={iFrameUrl}
        onClose={handleModalClose}
      />
      <div {...blockProps}>
        {calculatorId ? (
          <>
            <CalculatorInspector
              localCalcId={localCalcId}
              currentCalcId={calculatorId}
              onCalcIdChange={(value: string) => {
                setLocalCalcId(value);
                if (!isValidCalculatorId(value)) {
                  setErrorMessage(
                    'Invalid Calculator ID. Please navigate to Share → Embed in Convert_ to copy your ID.',
                  );
                } else {
                  setErrorMessage('');
                }
              }}
              onEditClick={handleEdit}
              onEmbedClick={handleEmbed}
              onSelectCalculatorClick={() =>
                handleOpenModal(`${BASE_URL}/calculator-overview/`)
              }
              errorMessage={errorMessage}
            />

            {isLoading ? (
              <Spinner />
            ) : (
              <div dangerouslySetInnerHTML={{ __html: renderedHTML }} />
            )}
          </>
        ) : (
          <>
            <CalculatorCreation
              localCalcId={localCalcId}
              onCalcIdChange={(value: string) => {
                setLocalCalcId(value);
                if (!isValidCalculatorId(value)) {
                  setErrorMessage(
                    'Invalid Calculator ID. Please navigate to Share → Embed in Convert_ to copy your ID.',
                  );
                } else {
                  setErrorMessage('');
                }
              }}
              onSelectTemplate={handleSelectTemplate}
              onCreateCalculator={handleCreateCalculator}
              onSelectCustomCalculators={handleSelectCustomCalculators}
              onEmbedCalculator={handleEmbed}
              onEditCalculator={handleEdit}
              errorMessage={errorMessage}
            />
          </>
        )}
      </div>
    </>
  );
}
